// Shipment table rows for one Excel report: filters, debounced search, reload and "Copy table"
import { useState, useEffect } from "react";
import api from "../../../utils/api";
import { rowsToTsv, copyText } from "./excelData";

const EMPTY = { rows: [], total: 0, options: {} };

export default function useExcelRows({ reportType, report, statusLabels }) {
    const uploadId = report?.uploadId;
    const [rows, setRows]               = useState(EMPTY);
    const [loading, setLoading]         = useState(false);
    const [search, setSearch]           = useState("");
    const [searchScope, setSearchScope] = useState("all");
    const [rowFilter, setRowFilter]     = useState("");
    const [facets, setFacets]           = useState({});
    const [version, setVersion]         = useState(0);   // bump to reload (e.g. after a comment)
    const [copyMsg, setCopyMsg]         = useState(null);

    const params = {
        reportType, q: search || undefined, scope: searchScope,
        filter: rowFilter || undefined, ...facets,
    };

    useEffect(() => {
        if (reportType === "platform" || !report) return;
        let alive = true;
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const res = await api.get("/api/excel-ingestion/shipments", { params: { ...params, limit: 200 } });
                if (alive) setRows(res.data);
            } catch {
                if (alive) setRows(EMPTY);
            } finally {
                if (alive) setLoading(false);
            }
        }, search ? 300 : 0);
        return () => { alive = false; clearTimeout(t); };
        // params is rebuilt every render from the values listed here
    }, [reportType, search, searchScope, rowFilter, facets, uploadId, version]);

    // One click: copy every row matching the current filters as tab-separated text (pastes into Excel)
    const copyTable = async () => {
        setCopyMsg("Copying…");
        try {
            const res = await api.get("/api/excel-ingestion/shipments", { params: { ...params, limit: 5000 } });
            await copyText(rowsToTsv(reportType, res.data.rows, statusLabels));
            setCopyMsg(`Copied ${res.data.rows.length.toLocaleString()} rows`);
        } catch {
            setCopyMsg("Copy failed");
        }
        setTimeout(() => setCopyMsg(null), 2500);
    };

    const setFacet = (name, value) => setFacets(f => {
        const next = { ...f };
        if (value) next[name] = value; else delete next[name];
        return next;
    });
    const resetFilters = () => { setFacets({}); setSearch(""); setRowFilter(""); };

    return {
        rows, loading,
        search, setSearch, searchScope, setSearchScope, rowFilter, setRowFilter, facets, setFacet,
        filtersActive: !!(search || rowFilter || Object.keys(facets).length),
        resetFilters,
        reload: () => setVersion(v => v + 1),
        copyTable, copyMsg,
    };
}
